/* eslint-disable typescript-enum/no-enum */

import { Id, Optional } from '@vimeo/core/types/generic';

import { IVimeoApiConnection } from './api';
import { EComposerSessionType } from './composer_session';
import { IInteractionBaseFirebaseConfig, IInteractionToolsMetadata } from './interaction_tools';

/**
 * Auth options to login in interaction rooms.
 * Are based on room config / security preferences.
 */
export enum EInteractionAuthOption {
  ADMIN = 'admin',
  REGISTRANT = 'registrant',
  ANONYMOUS = 'anonymous',
  VIMEO = 'vimeo',
  GUEST = 'guest',
}

interface IEventRoomTrackingInfo {
  ownerId: Id;
  sessionId: string;
}

interface IVenueTrackingInfo {
  ownerId: Id;
  registrantId: Id;
  venueId: string;
  spaceId: string;
  sessionId: Optional<string>;
  isSpacePrivate: boolean;
  isSpaceBreakout: boolean;
}

type TRoomTrackingInfo = IEventRoomTrackingInfo | IVenueTrackingInfo;

/**
 * Metadata with
 * - URIs/connections related to interaction room
 * - misc object with tracking info
 */
export interface IInteractionRoomInfoMetadata {
  connections: {
    auth: IVimeoApiConnection;
    ingestStatus: IVimeoApiConnection;
    credentialsCheck: IVimeoApiConnection;
  };
  misc?: {
    tracking?: TRoomTrackingInfo;
  };
}

/**
 * Details about room privacy settings.
 */
export interface IInteractionRoomPrivacyDetail {
  privacy: string;
  error?: string;
}

/**
 * Room details describing information about current session.
 * Fully relies on room logic instead of managing events, clips, venues etc.
 */
export interface IInteractionRoomInfo {
  uri: string;
  roomId: Id;
  roomType: EComposerSessionType;
  roomHash: string;
  metadata: IInteractionRoomInfoMetadata;
  settings: {
    authOptions: Record<EInteractionAuthOption, boolean>;
    privacy: EInteractionRoomPrivacy;
  };
}

export enum EInteractionRoomPrivacy {
  PUBLIC = 'anybody',
  NOBODY = 'nobody',
  PASSWORD = 'password',
  UNLISTED = 'unlisted',
  EMBED_ONLY = 'embed_only',
}

export interface IUserAuthToken {
  jwt: string;
  iat: number;
  exp: number;
}

export interface ISavedUserAuthPreferences {
  roomId: Id;
  displayName: string;
  authToken: IUserAuthToken;
}

/**
 * Interaction application user details.
 * Contains flags and permissions describing auth in current context.
 */
export interface IInteractionApplicationUser {
  id: string;
  displayName: string;
  avatarUrl: string;
  isStaff?: boolean;
  isCreator?: boolean;
  link: string;
  capabilities: {
    canUseQnaModeration: boolean;
    canViewQnaModeration?: boolean;
    canUseBackstageChat?: boolean;
  };
  authToken?: IUserAuthToken;
}

/**
 * Interaction room credentials for specific auth profile.
 * Contains info for connection to firebase, user details in current context and room IDs.
 */
export interface IInteractionRoomCredentials {
  uri: string;
  roomId: Id;
  roomType: EComposerSessionType;
  roomHash: string;
  firebase: IInteractionBaseFirebaseConfig;
  user: IInteractionApplicationUser;
  metadata: IInteractionToolsMetadata;
}

export interface IAuthorizeInteractionUserParams {
  type: EInteractionAuthOption;
  roomDisplayName?: string;
  roomRegistrantUuid?: string;
  roomGuestCode?: string;
  roomEmail?: string;
  password?: string;
  roomSavedUserAuthToken?: string;
}
